import { t } from "@/context/language";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { useEffect, useRef } from "react";

const MERRIWEATHER = { fontFamily: "var(--font-merriweather)" } as const;

type Metric = {
  label: string;
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
};

// Valores e formato fixos; os rótulos vêm das traduções (METRIC_LABELS index).
const METRIC_VALUES: Omit<Metric, "label">[] = [
  { value: 1496136 },
  { value: 480345 },
  { value: 32.1, decimals: 2, suffix: "%" },
  { value: 98, suffix: "%" },
];

function formatNumber(value: number, decimals: number) {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function AnimatedValue({
  value,
  decimals = 0,
  prefix = "",
  suffix = "",
  start,
}: Metric & { start: boolean }) {
  const count = useMotionValue(0);
  const formatted = useTransform(count, latest =>
    formatNumber(latest, decimals)
  );

  useEffect(() => {
    if (!start) return;
    const controls = animate(count, value, { duration: 1.8, ease: "easeOut" });
    return () => controls.stop();
  }, [start, value, count]);

  return (
    <>
      <span className="sr-only">
        {prefix}
        {formatNumber(value, decimals)}
        {suffix}
      </span>
      <span aria-hidden="true">
        {prefix}
        <motion.span>{formatted}</motion.span>
        {suffix}
      </span>
    </>
  );
}

export function BigNumbers() {
  const listRef = useRef<HTMLDListElement>(null);
  const inView = useInView(listRef, { once: true, margin: "-120px" });

  const metrics: Metric[] = METRIC_VALUES.map((m, i) => ({
    ...m,
    label: t.bigNumbers.metrics[i],
  }));

  return (
    <div className="py-12 lg:py-20">
      <motion.header
        className="max-w-2xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <h2
          className="mt-3 text-3xl leading-tight font-bold text-black sm:text-4xl lg:text-5xl"
          style={MERRIWEATHER}
        >
          {t.bigNumbers.title}
        </h2>
        <p className="mt-4 text-base text-gray-600 sm:text-lg">
          {t.bigNumbers.subtitle}
        </p>
      </motion.header>

      <dl
        ref={listRef}
        className="mt-12 border-t border-gray-200 lg:mt-16"
      >
        {metrics.map(metric => (
          <div
            key={metric.label}
            className="flex flex-col gap-2 border-b border-gray-200 py-7 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8 sm:py-8"
          >
            <dt className="text-primary max-w-xs text-base font-medium sm:text-lg">
              {metric.label}
            </dt>
            <dd
              className="text-4xl font-bold tracking-tight text-black tabular-nums sm:text-right sm:text-5xl lg:text-6xl xl:text-7xl"
              style={MERRIWEATHER}
            >
              <AnimatedValue {...metric} start={inView} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
